import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { z } from "zod";
import { smbProofSchema, smbPublicContactSchema } from "@shared/smb-intake";
import { smbHomeGenerateSchema, smbHomeOverridesSchema, smbHomePreviewSchema } from "@shared/smb-home-review";
import type { SmbHomeOverrides, SmbHomePreview } from "@shared/smb-home-review";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "wouter";
import { AlertCircle, ArrowUpRight, Home, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { withLpExpressSurfaceUrl } from "@/lib/lp-express-surface";

type Props = {
  brandId: string;
  eligible: boolean;
  eligibilityError?: boolean;
  onRetryEligibility?: () => void;
  designSystemReady: boolean;
  selectedRunId: string;
  selectedPageId: string;
  selectedPageReady: boolean;
  initiallyExpanded?: boolean;
  onAdmitted: (brandId: string, result: { runId?: string; pageId?: string }) => void;
};

type GenerateResponse = { runId?: string; pageId?: string; pending?: boolean };
type SmbHomeAttempt = { fingerprint: string; id: string; payload: Record<string, unknown>;
  photos?: Array<{ id: string; url: string; name: string }> };
type Proof = z.infer<typeof smbProofSchema>;
type HomePhoto = { key: string; file: File; previewUrl: string; status: "uploading" | "ready" | "error"; id?: string; url?: string; error?: string };
const optional = (value: string) => value.trim() ? value.trim() : undefined;
const ATTEMPT_STORAGE_PREFIX = "lp-express-smb-home-attempt:";

function freezePayload<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value as Record<string, unknown>).forEach((child) => freezePayload(child));
    Object.freeze(value);
  }
  return value;
}

export function LpExpressSmbHomeGenerator({
  brandId, eligible, eligibilityError = false, onRetryEligibility, designSystemReady,
  selectedRunId, selectedPageId, selectedPageReady, initiallyExpanded = true, onAdmitted,
}: Props) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactAddress, setContactAddress] = useState("");
  const [contactHours, setContactHours] = useState("");
  const [tone, setTone] = useState("");
  const [claims, setClaims] = useState("");
  const [rating, setRating] = useState("");
  const [reviewCount, setReviewCount] = useState("");
  const [years, setYears] = useState("");
  const [licensed, setLicensed] = useState<"" | "yes" | "no">("");
  const [testimonials, setTestimonials] = useState<Proof["testimonials"]>([]);
  const [clearContact, setClearContact] = useState(false);
  const [clearProof, setClearProof] = useState(false);
  const [clearTone, setClearTone] = useState(false);
  const [clearClaims, setClearClaims] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [saveToBrand, setSaveToBrand] = useState(false);
  const [recoveredPhotos, setRecoveredPhotos] = useState<NonNullable<SmbHomeAttempt["photos"]>>([]);
  const [photos, setPhotos] = useState<HomePhoto[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState<{ runId?: string; pageId?: string } | null>(null);
  const [acceptedResolved, setAcceptedResolved] = useState(false);
  const photoUrlsRef = useRef(new Set<string>());
  const lockRef = useRef(false);
  const attemptRef = useRef<SmbHomeAttempt | null>(null);
  const attemptsByBrandRef = useRef(new Map<string, SmbHomeAttempt>());
  const activeBrandRef = useRef(brandId);
  const brandRef = useRef(brandId);
  brandRef.current = brandId;
  const firstBrandRenderRef = useRef(true);
  const admittedRef = useRef(onAdmitted);
  admittedRef.current = onAdmitted;

  const preview = useQueryPreview(brandId, eligible);
  const switchingBrand = activeBrandRef.current !== brandId;

  const readStoredAttempt = (forBrand: string): SmbHomeAttempt | null => {
    try {
      const value = sessionStorage.getItem(`${ATTEMPT_STORAGE_PREFIX}${forBrand}`);
      if (!value) return null;
      const stored = JSON.parse(value) as SmbHomeAttempt;
      return smbHomeGenerateSchema.safeParse({ ...stored.payload, requestIdentity: stored.id }).success ? stored : null;
    } catch {
      return null;
    }
  };

  const persistAttempt = (forBrand: string, attempt: SmbHomeAttempt | null) => {
    try {
      if (attempt) sessionStorage.setItem(`${ATTEMPT_STORAGE_PREFIX}${forBrand}`, JSON.stringify(attempt));
      else sessionStorage.removeItem(`${ATTEMPT_STORAGE_PREFIX}${forBrand}`);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (firstBrandRenderRef.current) {
      firstBrandRenderRef.current = false;
      return;
    }
    const previousBrandId = activeBrandRef.current;
    if (previousBrandId !== brandId) {
      if (attemptRef.current) attemptsByBrandRef.current.set(previousBrandId, attemptRef.current);
      activeBrandRef.current = brandId;
      attemptRef.current = attemptsByBrandRef.current.get(brandId) ?? readStoredAttempt(brandId);
      if (attemptRef.current) attemptsByBrandRef.current.set(brandId, attemptRef.current);
      lockRef.current = false;
      setSubmitting(false);
    }
    setExpanded(true);
    setSelectedTemplate("");
    setInstructions("");
    setSaveToBrand(false);
    setRecoveredPhotos([]);
    photoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    photoUrlsRef.current.clear();
    setPhotos([]);
    setContactPhone("");
    setContactEmail("");
    setContactAddress("");
    setContactHours("");
    setTone("");
    setClaims("");
    setRating("");
    setReviewCount("");
    setYears("");
    setLicensed("");
    setTestimonials([]);
    setClearContact(false);
    setClearProof(false);
    setClearTone(false);
    setClearClaims(false);
    setDetailsOpen(false);
    setSubmitError("");
    setFormError("");
    setAccepted(null);
    setAcceptedResolved(false);
    setSubmitError(attemptRef.current
      ? "This brand has an unresolved request. Retry to resume the same saved payload and identity."
      : "");
  }, [brandId]);

  useEffect(() => () => {
    photoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    photoUrlsRef.current.clear();
  }, []);

  useEffect(() => {
    if (!attemptRef.current) {
      const stored = readStoredAttempt(brandId);
      if (stored) {
        attemptRef.current = stored;
        attemptsByBrandRef.current.set(brandId, stored);
        setSubmitError("This brand has an unresolved request. Retry to resume the same saved payload and identity.");
      }
    }
    const attempt = attemptRef.current;
    if (attempt) {
      setInstructions(typeof attempt.payload.instructions === "string" ? attempt.payload.instructions : "");
      setSelectedTemplate(typeof attempt.payload.catalogKey === "string" ? attempt.payload.catalogKey : "");
      setRecoveredPhotos(attempt.photos ?? []);
    }
  }, [brandId]);

  useEffect(() => {
    if (
      accepted &&
      selectedPageReady &&
      ((accepted.runId && accepted.runId === selectedRunId) ||
        (!accepted.runId && accepted.pageId && accepted.pageId === selectedPageId))
    ) setAcceptedResolved(true);
  }, [accepted, selectedRunId, selectedPageId, selectedPageReady]);

  useEffect(() => {
    const facts = preview.data?.facts;
    if (!facts) return;
    setContactPhone(facts.publicContact?.phone ?? "");
    setContactEmail(facts.publicContact?.email ?? "");
    setContactAddress(facts.publicContact?.address ?? "");
    setContactHours(facts.publicContact?.hours ?? "");
    setTone(facts.tone ?? "");
    setClaims(facts.claimsToAvoid ?? "");
    setRating(facts.proofNotes?.googleRating === undefined ? "" : String(facts.proofNotes.googleRating));
    setReviewCount(facts.proofNotes?.reviewCount === undefined ? "" : String(facts.proofNotes.reviewCount));
    setYears(facts.proofNotes?.yearsInBusiness === undefined ? "" : String(facts.proofNotes.yearsInBusiness));
    setLicensed(facts.proofNotes?.licensed === undefined ? "" : facts.proofNotes.licensed ? "yes" : "no");
    setTestimonials(facts.proofNotes?.testimonials ?? []);
  }, [preview.data, brandId]);

  useEffect(() => {
    const firstAvailable = preview.data?.templates.find((item) => item.eligible);
    if (firstAvailable && !preview.data?.templates.some((item) => item.eligible && item.key === selectedTemplate))
      setSelectedTemplate(firstAvailable.key);
  }, [preview.data, selectedTemplate]);

  const proofValue = useMemo(() => {
    const raw: Record<string, unknown> = {};
    if (optional(rating)) raw.googleRating = Number(rating);
    if (optional(reviewCount)) raw.reviewCount = Number(reviewCount);
    if (optional(years)) raw.yearsInBusiness = Number(years);
    if (licensed) raw.licensed = licensed === "yes";
    if (testimonials?.length) raw.testimonials = testimonials;
    return smbProofSchema.safeParse(raw);
  }, [rating, reviewCount, years, licensed, testimonials]);

  const previewFact = preview.data?.facts ?? {};
  const availableTemplates = preview.data?.templates.filter((template) => template.eligible) ?? [];
  const selected = availableTemplates.find((item) => item.key === selectedTemplate);
  const proofReady = clearProof || proofValue.success;

  const retryLocked = Boolean(attemptRef.current && (submitError || submitting));
  const formLocked = retryLocked || !!accepted;
  const uploadPhoto = async (photo: HomePhoto, forBrand = brandId) => {
    setPhotos((rows) => rows.map((row) => row.key === photo.key ? { ...row, status: "uploading", error: undefined } : row));
    try {
      const form = new FormData();
      form.append("file", photo.file);
      const response = await fetch(withLpExpressSurfaceUrl(
        `/api/brands/${forBrand}/lp-express/smb-home-photos`,
        "lp-express-2",
      ), { method: "POST", body: form, credentials: "include" });
      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new Error(typeof problem?.message === "string" ? problem.message
          : "Photo upload failed. Retry, or choose a PNG, JPEG or WebP under 10 MB.");
      }
      const uploaded = await response.json() as { id?: string; url?: string };
      if (!uploaded.id || !z.string().uuid().safeParse(uploaded.id).success || !uploaded.url)
        throw new Error("The upload response was incomplete.");
      if (brandRef.current !== forBrand) return;
      setPhotos((rows) => rows.map((row) => row.key === photo.key
        ? { ...row, status: "ready", id: uploaded.id, url: uploaded.url, error: undefined }
        : row));
    } catch (cause) {
      if (brandRef.current === forBrand) {
        setPhotos((rows) => rows.map((row) => row.key === photo.key
          ? { ...row, status: "error", error: cause instanceof Error ? cause.message : "Photo upload failed." }
          : row));
      }
    }
  };

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const remaining = Math.max(0, 6 - photos.length);
    const pickedFiles = Array.from(files);
    const acceptedFiles = pickedFiles.slice(0, remaining);
    const tooLarge = acceptedFiles.find((file) => file.size > 10 * 1024 * 1024);
    if (tooLarge) setFormError(`${tooLarge.name} is larger than the 10 MB photo limit.`);
    else if (pickedFiles.length > remaining) setFormError("A Home page can include up to 6 photos.");
    else setFormError("");
    const validFiles = acceptedFiles.filter((file) => file.size <= 10 * 1024 * 1024);
    const next = validFiles.map((file) => ({
      key: crypto.randomUUID(), file, previewUrl: URL.createObjectURL(file), status: "uploading" as const,
    }));
    next.forEach((photo) => photoUrlsRef.current.add(photo.previewUrl));
    setPhotos((rows) => [...rows, ...next]);
    next.forEach((photo) => void uploadPhoto(photo));
  };

  const submit = async () => {
    if (lockRef.current) return;
    const resuming = attemptRef.current;
    if (!resuming && (!selectedTemplate || !selected)) {
      setFormError("Choose an available Home page template.");
      return;
    }
    if (!resuming && !proofReady) {
      setDetailsOpen(true);
      setFormError(proofValue.success ? "" : `Proof needs review: ${proofValue.error.issues[0]?.message ?? "check the values."}`);
      return;
    }
    if (!resuming && photos.some((photo) => photo.status !== "ready" || !photo.id)) {
      setFormError("Wait for every photo to finish uploading, or retry/remove the failed photo.");
      return;
    }
    if (!resuming && !designSystemReady) {
      setFormError("Wait for the brand design-system selection to save before generating.");
      return;
    }
    let attempt = resuming;
    if (!attempt) {
    const publicContact = {
      ...(optional(contactPhone) ? { phone: optional(contactPhone) } : {}),
      ...(optional(contactEmail) ? { email: optional(contactEmail) } : {}),
      ...(optional(contactAddress) ? { address: optional(contactAddress) } : {}),
      ...(optional(contactHours) ? { hours: optional(contactHours) } : {}),
    };
    const contactCheck = smbPublicContactSchema.safeParse(publicContact);
    if (!contactCheck.success) {
      setDetailsOpen(true);
      setFormError(contactCheck.error.issues[0]?.message ?? "Check public contact details.");
      return;
    }
    const overrides: SmbHomeOverrides = {};
    if (clearContact) overrides.publicContact = null;
    else if (JSON.stringify(contactCheck.data) !== JSON.stringify(previewFact.publicContact ?? {})) overrides.publicContact = Object.keys(publicContact).length ? contactCheck.data : null;
    if (clearProof) overrides.proofNotes = null;
    else if (JSON.stringify(proofValue.data) !== JSON.stringify(previewFact.proofNotes ?? {})) overrides.proofNotes = Object.keys(proofValue.data ?? {}).length ? proofValue.data : null;
    if (clearTone) overrides.tone = null;
    else if ((optional(tone) ?? "") !== (previewFact.tone ?? "")) overrides.tone = optional(tone) ?? null;
    if (clearClaims) overrides.claimsToAvoid = null;
    else if ((optional(claims) ?? "") !== (previewFact.claimsToAvoid ?? "")) overrides.claimsToAvoid = optional(claims) ?? null;
    const overridesCheck = smbHomeOverridesSchema.safeParse(overrides);
    if (!overridesCheck.success) {
      setFormError(overridesCheck.error.issues[0]?.message ?? "Check the business facts before generating.");
      return;
    }
    const payload: Record<string, unknown> = {
      catalogKey: selectedTemplate,
      saveToBrand,
      ...(Object.keys(overridesCheck.data).length ? { overrides: overridesCheck.data } : {}),
      ...(optional(instructions) ? { instructions: optional(instructions) } : {}),
      ...(photos.length ? { photoIds: photos.map((photo) => photo.id!) } : {}),
    };
    const fingerprint = JSON.stringify({ brandId, payload });
    attempt = { fingerprint, id: crypto.randomUUID(), payload: freezePayload({ ...payload }),
      photos: photos.map(photo => ({ id: photo.id!, url: photo.url!, name: photo.file.name })) };
    if (!persistAttempt(brandId, attempt)) {
      setFormError("Allow session storage in this browser before generating so a lost response can be retried safely.");
      return;
    }
    attemptRef.current = attempt;
    attemptsByBrandRef.current.set(brandId, attempt);
    }
    lockRef.current = true;
    setSubmitting(true);
    setFormError("");
    setSubmitError("");
    try {
      const response = await apiRequest(
        "POST",
        withLpExpressSurfaceUrl(`/api/brands/${brandId}/lp-express/generate-smb-home`, "lp-express-2"),
        { ...attempt.payload, requestIdentity: attempt.id },
      );
      const result = await parseJsonResponse(response) as GenerateResponse;
      if (!result.runId && !result.pageId) throw new Error("The page was accepted without a run or page reference. Reload this brand before trying again.");
      persistAttempt(brandId, null);
      attemptsByBrandRef.current.delete(brandId);
      if (brandRef.current !== brandId) return;
      attemptRef.current = null;
      setAccepted({ runId: result.runId, pageId: result.pageId });
      admittedRef.current(brandId, result);
    } catch (error) {
      const failure = describeSmbHomeFailure(error);
      if (failure.rejectedBeforeAdmission) {
        if (brandRef.current === brandId) attemptRef.current = null;
        attemptsByBrandRef.current.delete(brandId);
        persistAttempt(brandId, null);
      }
      if (brandRef.current === brandId) {
        setSubmitError(failure.message);
      }
    } finally {
      if (brandRef.current === brandId) {
        lockRef.current = false;
        setSubmitting(false);
      }
    }
  };

  if (eligibilityError) {
    return (
      <div className="mt-3 rounded-xl border border-amber-900/15 bg-[#f8f6ee] p-4 text-sm text-teal-950/80" data-testid="smb-home-access-check-error">
        <p>Home page availability could not be checked.</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={onRetryEligibility}>Check again</Button>
      </div>
    );
  }
  if (!eligible) return null;

  return (
    <Card className="mt-3 border-teal-900/15 bg-[#f6faf8] shadow-none" data-testid="smb-home-generator">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-teal-950 text-[#e2efe9]">
            <Home className="h-4 w-4" />
          </span>
          <div>
            <CardTitle className="text-sm font-semibold text-teal-950">SMB Home page</CardTitle>
          <p className="mt-0.5 text-xs text-teal-950/65">Build from saved business facts and supplied photos.</p>
          </div>
        </div>
        <Button size="sm" variant={expanded ? "outline" : "default"} onClick={() => setExpanded((open) => !open)}>
          {expanded ? "Hide setup" : <><Sparkles className="mr-1.5 h-3.5 w-3.5" />Create Home page</>}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-5 border-t border-teal-900/10 px-4 py-4">
          {preview.isLoading && <div className="space-y-2" aria-label="Loading business facts"><div className="h-3 w-2/3 animate-pulse rounded bg-teal-900/10" /><div className="h-3 w-full animate-pulse rounded bg-teal-900/10" /><div className="h-20 animate-pulse rounded-lg bg-teal-900/10" /></div>}
          {preview.error && <InlineError text="Business facts could not be loaded." retry={preview.refetch} />}
          {preview.data && !switchingBrand && (
            <>
              <fieldset disabled={formLocked} className="min-w-0 space-y-5 disabled:opacity-75">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <Label>Home page template</Label>
                  {availableTemplates.length ? (
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger data-testid="smb-home-template"><SelectValue placeholder="Choose an available template" /></SelectTrigger>
                      <SelectContent>{availableTemplates.map((template) => <SelectItem value={template.key} key={template.key}>{template.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <div className="rounded-lg border border-dashed border-teal-900/20 px-3 py-3 text-sm text-teal-950/70">
                      <p>No Home page templates are available for this brand yet.</p>
                      <Button className="mt-2" type="button" size="sm" variant="outline" onClick={preview.refetch}>Refresh templates</Button>
                    </div>
                  )}
                  {selected?.description && <p className="text-xs text-muted-foreground">{selected.description}</p>}
                </div>

                <section className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="smb-home-instructions">What should we know?</Label>
                    <span className="text-xs tabular-nums text-muted-foreground">{instructions.length}/4000</span>
                  </div>
                  <Textarea
                    id="smb-home-instructions"
                    value={instructions}
                    onChange={(event) => setInstructions(event.target.value.slice(0, 4000))}
                    maxLength={4000}
                    disabled={formLocked}
                    placeholder="Optional. Share what to emphasize, who you serve, or anything the page should get right."
                    className="min-h-32 resize-y bg-white/70"
                  />
                  <p className="text-xs text-muted-foreground">For this page only. Saved business facts remain the source of truth.</p>
                </section>

                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div><Label>Photos <span className="font-normal text-muted-foreground">· optional</span></Label><p className="mt-1 text-xs text-muted-foreground">Up to 6 photos, 10 MB each.</p></div>
                    <Button type="button" variant="outline" size="sm" disabled={formLocked || photos.length >= 6} onClick={() => document.getElementById("smb-home-photo-input")?.click()}>
                      <ImagePlus className="mr-2 h-4 w-4" />Add photos
                    </Button>
                    <input id="smb-home-photo-input" className="sr-only" type="file" accept="image/*" multiple disabled={formLocked || photos.length >= 6} onChange={(event) => { addPhotos(event.currentTarget.files); event.currentTarget.value = ""; }} />
                  </div>
                  {photos.length > 0 && <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {photos.map((photo) => (
                      <li key={photo.key} className="overflow-hidden rounded-xl border border-teal-900/10 bg-white/70">
                        <div className="relative aspect-[4/3] bg-teal-950/5">
                          <img src={photo.previewUrl} alt={photo.file.name} className="h-full w-full object-cover" />
                          <Button type="button" variant="secondary" size="icon" className="absolute right-2 top-2 h-7 w-7" aria-label={`Remove ${photo.file.name}`} disabled={formLocked} onClick={() => { URL.revokeObjectURL(photo.previewUrl); photoUrlsRef.current.delete(photo.previewUrl); setPhotos((rows) => rows.filter((row) => row.key !== photo.key)); }}><X className="h-4 w-4" /></Button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                          <span className="truncate">{photo.file.name}</span>
                          <span className={photo.status === "error" ? "shrink-0 text-destructive" : "shrink-0 text-muted-foreground"} role="status">
                            {photo.status === "uploading" ? "Uploading…" : photo.status === "ready" ? "Ready" : "Failed"}
                          </span>
                        </div>
                        {photo.status === "error" && <div className="flex items-center justify-between gap-2 px-3 pb-2">
                          <span className="truncate text-xs text-destructive">{photo.error}</span>
                          <Button type="button" size="sm" variant="ghost" className="h-7 shrink-0 px-2" onClick={() => void uploadPhoto(photo)}>Retry</Button>
                        </div>}
                      </li>
                    ))}
                  </ul>}
                  {!photos.length && recoveredPhotos.length > 0 && <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-label="Saved request photos">
                    {recoveredPhotos.map(photo => <li key={photo.id} className="overflow-hidden rounded-xl border bg-white/70">
                      <img src={photo.url} alt={photo.name} className="aspect-[4/3] w-full object-cover" />
                      <p className="truncate px-3 py-2 text-xs">{photo.name} · Saved with this request</p>
                    </li>)}
                  </ul>}
                </section>

                <details open={detailsOpen} onToggle={(event) => setDetailsOpen(event.currentTarget.open)} className="rounded-xl border border-teal-900/10 bg-white/40">
                  <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-teal-950 marker:hidden">
                    <span className="flex items-center justify-between">Review saved business details <span className="text-xs font-normal text-teal-950/60">Optional</span></span>
                  </summary>
                  <div className="space-y-5 border-t border-teal-900/10 p-4">
                  <div className="grid gap-4 md:grid-cols-[1.3fr_.7fr]">
                  <div className="space-y-3">
                  <div className="rounded-xl border border-teal-900/10 bg-white/70 p-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-950/60">Business snapshot</p>
                    <p className="whitespace-pre-line text-sm leading-6 text-teal-950/80">{preview.data.businessInfoPreview || "No business summary is available yet."}</p>
                    {preview.data.missing.length > 0 && <p className="mt-2 text-xs text-amber-800">Missing: {preview.data.missing.join(", ")}</p>}
                    {preview.data.warnings.length > 0 && <p className="mt-2 text-xs text-amber-800">{preview.data.warnings.join(" ")}</p>}
                  </div>
                </div>
                <div className="rounded-xl border border-teal-900/10 bg-white/70 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-950/60">Included as-is</p>
                    <Link className="inline-flex items-center gap-1 text-xs font-medium text-teal-800 underline-offset-2 hover:underline" href={`/brands/${brandId}/settings`}>
                      Edit brand facts <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </div>
                  <p className="mt-3 text-xs font-medium text-teal-950">Services</p>
                  <ul className="mt-1 space-y-1 text-sm text-teal-950/75">{preview.data.services.length ? preview.data.services.map((service) => <li key={service.id}>{service.name}</li>) : <li>No services on file.</li>}</ul>
                  <p className="mt-3 text-xs font-medium text-teal-950">Service areas</p>
                  <p className="mt-1 text-sm text-teal-950/75">{preview.data.serviceAreas.length ? preview.data.serviceAreas.join(", ") : "No service areas on file."}</p>
                  <Link className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-teal-800 underline-offset-2 hover:underline" href={`/brands/${brandId}/settings`}>
                    Update services and areas <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>

              <section className="space-y-3">
                <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold text-teal-950">Public contact details</h3><ClearButton active={clearContact} onClick={() => setClearContact((value) => !value)} /></div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Phone"><Input value={contactPhone} placeholder="Public contact phone" onChange={(event) => { setContactPhone(event.target.value); setClearContact(false); }} /></Field>
                  <Field label="Public email"><Input value={contactEmail} onChange={(event) => { setContactEmail(event.target.value); setClearContact(false); }} /></Field>
                  <Field label="Address"><Input value={contactAddress} onChange={(event) => { setContactAddress(event.target.value); setClearContact(false); }} /></Field>
                  <Field label="Hours"><Input value={contactHours} onChange={(event) => { setContactHours(event.target.value); setClearContact(false); }} /></Field>
                </div>
              </section>

              <div className="grid gap-4 md:grid-cols-2">
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold text-teal-950">Structured proof</h3><ClearButton active={clearProof} onClick={() => setClearProof((value) => !value)} /></div>
                  <p className="text-xs text-muted-foreground">Only supplied facts are used. Testimonials remain verbatim.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Rating"><Input inputMode="decimal" value={rating} onChange={(event) => { setRating(event.target.value); setClearProof(false); }} placeholder="0 to 5" /></Field>
                    <Field label="Review count"><Input inputMode="numeric" value={reviewCount} onChange={(event) => { setReviewCount(event.target.value); setClearProof(false); }} /></Field>
                    <Field label="Years in business"><Input inputMode="decimal" value={years} onChange={(event) => { setYears(event.target.value); setClearProof(false); }} /></Field>
                    <Field label="Licensed"><Select value={licensed || "unset"} onValueChange={(value) => { setLicensed(value === "unset" ? "" : value as "yes" | "no"); setClearProof(false); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unset">Not stated</SelectItem><SelectItem value="yes">Licensed</SelectItem><SelectItem value="no">Not licensed</SelectItem></SelectContent></Select></Field>
                  </div>
                  {(testimonials ?? []).map((testimonial, index) => (
                    <div className="grid gap-2 rounded-lg bg-white/70 p-2" key={index}>
                      <Textarea aria-label={`Testimonial ${index + 1}`} value={testimonial.quote} onChange={(event) => setTestimonials((rows) => rows?.map((row, rowIndex) => rowIndex === index ? { ...row, quote: event.target.value } : row))} />
                      <div className="flex gap-2"><Input aria-label={`Testimonial attribution ${index + 1}`} value={testimonial.attribution} onChange={(event) => setTestimonials((rows) => rows?.map((row, rowIndex) => rowIndex === index ? { ...row, attribution: event.target.value } : row))} /><Button variant="ghost" size="sm" onClick={() => setTestimonials((rows) => rows?.filter((_, rowIndex) => rowIndex !== index))}>Remove</Button></div>
                    </div>
                  ))}
                  {(testimonials?.length ?? 0) < 6 && <Button type="button" variant="outline" size="sm" onClick={() => setTestimonials((rows) => [...(rows ?? []), { quote: "", attribution: "" }])}>Add testimonial</Button>}
                  {!proofReady && <p className="text-xs text-destructive">{proofValue.success ? "" : proofValue.error.issues[0]?.message}</p>}
                  <p className="rounded-lg bg-teal-900/5 px-3 py-2 text-xs text-teal-950/70">No proof or supplied photos is fine. These are optional and will not block generation.</p>
                </section>
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold text-teal-950">Voice and guardrails</h3></div>
                  <Field label="Tone"><Input value={tone} onChange={(event) => { setTone(event.target.value); setClearTone(false); }} placeholder="Warm, direct, locally trusted" /><ClearButton active={clearTone} onClick={() => setClearTone((value) => !value)} /></Field>
                  <Field label="Claims to avoid"><Textarea value={claims} onChange={(event) => { setClaims(event.target.value); setClearClaims(false); }} placeholder="Anything the page must not claim" /><ClearButton active={clearClaims} onClick={() => setClearClaims((value) => !value)} /></Field>
                  <label className="flex items-start gap-2 rounded-lg border border-teal-900/10 bg-white/70 p-3 text-sm text-teal-950/80">
                    <input type="checkbox" checked={saveToBrand} onChange={event => setSaveToBrand(event.target.checked)} className="mt-1 accent-teal-900" />
                    <span><strong className="font-medium">Save these edits to brand facts</strong><span className="mt-0.5 block text-xs text-muted-foreground">Off by default. Only supported factual edits are saved, never instructions or attached photos.</span></span>
                  </label>
                </section>
              </div>
                  </div>
                </details>
              </div>
              </fieldset>
              {formError && <p role="alert" className="text-sm text-destructive">{formError}</p>}
              {!designSystemReady && <p role="status" className="text-sm text-amber-800">Wait for the brand design-system selection to save, or correct the selection above before generating.</p>}
              {submitError && <div role="alert" className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">{submitError} Retry submits the same saved request details and identity.</div>}
              {accepted && (
                <p className="rounded-lg bg-teal-900/5 px-3 py-2 text-sm text-teal-950/80" role="status">
                  {acceptedResolved ? "Your Home page is ready. Open the review to create another page." : "Your page was accepted. Opening the existing editor while its durable generation finishes."}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-teal-900/10 pt-4">
                {accepted && acceptedResolved ? (
                  <Button type="button" onClick={() => {
                    attemptRef.current = null;
                    attemptsByBrandRef.current.delete(brandId);
                    setAccepted(null);
                    setAcceptedResolved(false);
                    setSubmitError("");
                    setFormError("");
                    setExpanded(true);
                  }}>Create another Home page</Button>
                ) : (
                  <Button disabled={!!accepted || (!retryLocked && (!availableTemplates.length || !designSystemReady || !selected || photos.some((photo) => photo.status !== "ready" || !photo.id))) || submitting} onClick={() => void submit()} data-testid="button-smb-home-generate">
                    {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting page…</> : accepted ? "Page accepted" : retryLocked ? "Retry saved request" : "Generate Home page"}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export function SmbHomeWholePageRegenerate({
  brandId, pageId, catalogKey, currentCopy, onAdmitted,
}: {
  brandId: string;
  pageId: string;
  catalogKey: string;
  currentCopy: string;
  onAdmitted: (brandId: string, result: { runId?: string; pageId?: string }) => void;
}) {
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const lockRef = useRef(false);
  const identityRef = useRef<{ fingerprint: string; id: string; payload: Record<string, unknown> } | null>(null);
  const brandRef = useRef(brandId);
  brandRef.current = brandId;
  const admittedRef = useRef(onAdmitted);
  admittedRef.current = onAdmitted;
  useEffect(() => {
    setFeedback("");
    setError("");
    setAccepted(false);
    identityRef.current = null;
  }, [brandId, pageId]);

  const submit = async () => {
    if (lockRef.current || !feedback.trim()) return;
    const payload: Record<string, unknown> = {
      catalogKey,
      previousPageId: pageId,
      feedback: feedback.trim(),
      currentCopy,
    };
    const fingerprint = JSON.stringify({ brandId, payload });
    if (!identityRef.current) {
      identityRef.current = { fingerprint, id: crypto.randomUUID(), payload };
    }
    const attempt = identityRef.current;
    lockRef.current = true;
    setPending(true);
    setError("");
    try {
      const response = await apiRequest(
        "POST",
        withLpExpressSurfaceUrl(`/api/brands/${brandId}/lp-express/generate-smb-home`, "lp-express-2"),
        { ...attempt.payload, requestIdentity: attempt.id },
      );
      const result = await parseJsonResponse(response) as GenerateResponse;
      if (brandRef.current !== brandId) return;
      if (!result.runId && !result.pageId) throw new Error("The new page was accepted without a run reference. Reload this brand before trying again.");
      setAccepted(true);
      admittedRef.current(brandId, result);
      setFeedback("");
    } catch (cause) {
      if (brandRef.current === brandId) {
        const failure = describeSmbHomeFailure(cause);
        if (failure.rejectedBeforeAdmission) identityRef.current = null;
        setError(failure.message);
      }
    } finally {
      lockRef.current = false;
      setPending(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-teal-900/15 bg-[#f6faf8] p-4" data-testid="smb-home-whole-page-regenerate">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-teal-950"><Sparkles className="h-4 w-4" />Regenerate whole page with notes</div>
      <p className="mb-3 text-xs leading-5 text-teal-950/70">Creates a new retained page and leaves this version unchanged. This may incur paid Studio generation charges.</p>
      <Textarea disabled={Boolean((identityRef.current && error) || accepted || pending)} value={feedback} onChange={(event) => setFeedback(event.target.value)} placeholder="What should change across the full page?" maxLength={4000} />
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error} Retry submits the same saved notes and identity.</p>}
      {accepted && <p className="mt-2 text-sm text-teal-950/75" role="status">The new page was accepted. This action stays locked until its owning page changes.</p>}
      <Button className="mt-3" variant="outline" disabled={accepted || ((!identityRef.current && !feedback.trim()) || pending)} onClick={() => void submit()} data-testid="button-smb-home-regenerate">
        {pending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting new page…</> : accepted ? "New page accepted" : "Regenerate whole page"}
      </Button>
    </div>
  );
}

function useQueryPreview(brandId: string, enabled: boolean) {
  const [data, setData] = useState<SmbHomePreview | undefined>();
  const [state, setState] = useState({ loading: false, error: false });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    if (!enabled) { setData(undefined); setState({ loading: false, error: false }); return; }
    setData(undefined);
    setState({ loading: true, error: false });
    fetch(`/api/brands/${brandId}/lp-express/smb-home-preview-info?surface=lp-express-2`, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error("Preview load failed");
        return smbHomePreviewSchema.parse(await response.json()) as SmbHomePreview;
      })
      .then((value) => { if (active) { setData(value); setState({ loading: false, error: false }); } })
      .catch(() => { if (active) setState({ loading: false, error: true }); });
    return () => { active = false; };
  }, [brandId, enabled, version]);
  return { data, isLoading: state.loading, error: state.error, refetch: () => setVersion((value) => value + 1) };
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}

function ClearButton({ active, onClick }: { active: boolean; onClick: () => void }) {
  return <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={onClick}>{active ? "Will clear" : "Clear saved value"}</Button>;
}

function InlineError({ text, retry }: { text: string; retry: () => void }) {
  return <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{text}</span><Button variant="outline" size="sm" onClick={retry}>Retry</Button></div>;
}

function describeSmbHomeFailure(error: unknown): { message: string; rejectedBeforeAdmission: boolean } {
  const message = error instanceof Error ? error.message : "";
  const match = message.match(/^(\d{3}):\s*([\s\S]*)$/);
  const status = match ? Number(match[1]) : 0;
  const body = match?.[2];
  if (body) {
    try {
      const parsed = JSON.parse(body) as {
        code?: unknown;
        message?: unknown;
        admission?: { runId?: unknown; pageId?: unknown };
      };
      const safeMessage = typeof parsed.message === "string" ? parsed.message : "";
      const code = typeof parsed.code === "string" ? parsed.code : "";
      if (
        parsed.code === "smb_home_save_failed" &&
        parsed.admission &&
        (typeof parsed.admission.runId === "string" || typeof parsed.admission.pageId === "string")
      ) {
        const reference = typeof parsed.admission.runId === "string"
          ? `run ${parsed.admission.runId}`
          : `page ${parsed.admission.pageId}`;
        return {
          message: `The page was accepted as ${reference}, but the explicit business-fact save did not finish (${code}). Retry unchanged details to resume this request.`,
          rejectedBeforeAdmission: false,
        };
      }
      if (safeMessage) {
        return {
          message: code ? `${safeMessage} (${code})` : safeMessage,
          rejectedBeforeAdmission: status >= 400 && status < 500 && !parsed.admission,
        };
      }
      return {
        message: "The server could not confirm this page request. Retry the same details to safely resume it.",
        rejectedBeforeAdmission: status >= 400 && status < 500,
      };
    } catch {
      return {
        message: "The server could not confirm this page request. Retry the same details to safely resume it.",
        rejectedBeforeAdmission: status >= 400 && status < 500,
      };
    }
  }
  if (!message || message.includes("<") || message.length > 300) {
    return {
      message: "The server could not confirm this page request. Retry the same details to safely resume it.",
      rejectedBeforeAdmission: false,
    };
  }
  return { message, rejectedBeforeAdmission: false };
}
